import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  X,
  Phone,
  MapPin,
  CreditCard,
  Landmark,
  Loader2,
  Power,
  ShieldCheck,
  FileImage,
  Hash,
  Calendar,
  ImageOff,
  ArrowLeft,
  ListChecks,
  Wallet,
  CalendarClock,
} from 'lucide-react';
import {
  getProviderById,
  updateProviderKyc,
  updateProviderStatus,
} from '../../services/adminproviders.api';
import {
  ProviderDetail,
  KycStatus,
  KYC_STATUS_OPTIONS,
} from '../../types/pppadminproviders-model';

// ─── Small presentational helpers ──────────────────────────────────────────────

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}> = ({ icon, label, value, mono }) => (
  <div className="flex items-start gap-3 rounded-xl border border-stone-100 bg-stone-50/60 px-3.5 py-3">
    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white text-stone-400 shadow-sm ring-1 ring-stone-100">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-stone-400">{label}</div>
      <div className={`mt-0.5 truncate text-[13.5px] ${mono ? 'font-mono' : 'font-medium'} ${value ? 'text-stone-800' : 'text-stone-300'}`}>
        {value || 'Not provided'}
      </div>
    </div>
  </div>
);

const kycBadgeClasses = (status: KycStatus | null) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 ring-rose-200';
    case 'changes_requested':
      return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'pending':
      return 'bg-sky-50 text-sky-700 ring-sky-200';
    default:
      return 'bg-stone-100 text-stone-500 ring-stone-200';
  }
};

const kycLabel = (status: KycStatus | null) => {
  if (!status) return 'Not submitted';
  return KYC_STATUS_OPTIONS.find(o => o.value === status)?.label ?? status;
};

const DocCard: React.FC<{ label: string; url: string | null }> = ({ label, url }) => (
  <a
    href={url ?? undefined}
    target="_blank"
    rel="noreferrer"
    onClick={e => {
      if (!url) e.preventDefault();
    }}
    className={`group relative flex flex-col overflow-hidden rounded-xl border border-stone-200 bg-white transition ${
      url ? 'cursor-pointer hover:border-amber-300 hover:shadow-md' : 'cursor-default opacity-70'
    }`}
  >
    <div className="flex h-24 w-full items-center justify-center overflow-hidden bg-stone-100">
      {url ? (
        <img
          src={url}
          alt={label}
          className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
        />
      ) : (
        <ImageOff size={20} className="text-stone-300" />
      )}
    </div>
    <div className="border-t border-stone-100 px-2.5 py-2 text-center text-[10.5px] font-medium text-stone-500">
      {label}
    </div>
  </a>
);

// ─── Provider Detail Modal (route-driven) ──────────────────────────────────────
// Reads :id from the URL instead of receiving it as a prop. Closing the modal
// navigates back to /admin/providers instead of clearing local state.

const ProviderDetailModal: React.FC = () => {
  const { id: providerId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<ProviderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // KYC review form state
  const [kycStatusInput, setKycStatusInput] = useState<KycStatus>('approved');
  const [kycNoteInput, setKycNoteInput] = useState('');
  const [kycSaving, setKycSaving] = useState(false);
  const [kycError, setKycError] = useState('');
  const [kycSuccess, setKycSuccess] = useState(false);

  // Active/inactive toggle state
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');

  const handleClose = () => {
    navigate('/admin/providers');
  };

  const loadDetail = (id: string) => {
    setLoading(true);
    setError('');
    getProviderById(id)
      .then(d => {
        setDetail(d);
        setKycStatusInput(d.kycStatus && d.kycStatus !== 'pending' ? d.kycStatus : 'approved');
        setKycNoteInput(d.kycNote ?? '');
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!providerId) {
      setDetail(null);
      return;
    }
    loadDetail(providerId);
  }, [providerId]);

  const handleKycSubmit = async () => {
    if (!detail) return;
    setKycSaving(true);
    setKycError('');
    setKycSuccess(false);
    try {
      await updateProviderKyc(detail.id, {
        status: kycStatusInput,
        note: kycNoteInput.trim() ? kycNoteInput.trim() : null,
      });
      setKycSuccess(true);
      loadDetail(detail.id);
    } catch (err) {
      setKycError(err instanceof Error ? err.message : 'Failed to update KYC status.');
    } finally {
      setKycSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!detail) return;
    setStatusSaving(true);
    setStatusError('');
    try {
      await updateProviderStatus(detail.id, { isActive: !detail.isActive });
      loadDetail(detail.id);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to update provider status.');
    } finally {
      setStatusSaving(false);
    }
  };

  if (!providerId) return null;

  return (
    <div className="min-h-screen bg-stone-50 px-8 py-7">
      {/* Top bar: back navigation */}
      <div className="mb-5 flex items-center justify-between">
        <button
          onClick={handleClose}
          className="flex items-center gap-1.5 text-[13px] font-medium text-stone-500 transition hover:text-stone-800"
        >
          <ArrowLeft size={15} /> Back to Providers
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        {/* Header */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-amber-600 to-amber-500 px-8 py-7">
          {loading && (
            <div className="flex items-center gap-3 py-1">
              <Loader2 size={18} className="animate-spin text-white" />
              <span className="text-[13px] font-medium text-white/90">Loading provider…</span>
            </div>
          )}

          {!loading && error && (
            <div className="text-[13px] font-medium text-white/95">{error}</div>
          )}

          {!loading && detail && (
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 text-xl font-bold text-white ring-1 ring-white/30">
                {detail.fullname.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[19px] font-bold text-white">{detail.fullname}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-amber-50/90">
                  <span>{detail.usertype}</span>
                  <span className="opacity-50">·</span>
                  <Calendar size={11} />
                  <span>Joined {detail.edate}</span>
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold ring-1 ring-inset ${kycBadgeClasses(
                    detail.kycStatus
                  )}`}
                >
                  KYC: {kycLabel(detail.kycStatus)}
                </span>
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold ring-1 ring-inset ${
                    detail.isActive
                      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                      : 'bg-stone-100 text-stone-500 ring-stone-200'
                  }`}
                >
                  {detail.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Quick action links */}
        {!loading && detail && (
          <div className="flex flex-wrap items-center gap-2.5 border-b border-stone-100 bg-stone-50/70 px-8 py-3.5">
            <button
                onClick={() => navigate(`/admin/providers/${detail.id}/listings`)}
                className="flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-[12.5px] font-medium text-stone-600 shadow-sm transition hover:border-amber-300 hover:text-amber-700"
                >
                <ListChecks size={14} /> View Listings
                </button>
            <button className="flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-[12.5px] font-medium text-stone-600 shadow-sm transition hover:border-amber-300 hover:text-amber-700">
              <Wallet size={14} /> View Payment Details
            </button>
            <button className="flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-[12.5px] font-medium text-stone-600 shadow-sm transition hover:border-amber-300 hover:text-amber-700">
              <CalendarClock size={14} /> View Bookings
            </button>
          </div>
        )}

        {/* Body */}
        <div className="px-8 py-7">
          {!loading && detail && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-5">
              {/* Left column — info */}
              <div className="flex flex-col gap-6 lg:col-span-3">
                {/* Contact */}
                <section>
                  <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-stone-400">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <InfoRow icon={<Phone size={14} />} label="Mobile Number" value={detail.mobile} mono />
                    <InfoRow icon={<MapPin size={14} />} label="Address" value={detail.address} />
                  </div>
                </section>

                {/* KYC identifiers */}
                <section>
                  <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-stone-400">
                    Identity & Tax Details
                  </h3>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <InfoRow icon={<Hash size={14} />} label="Aadhar Number" value={detail.aadhar} mono />
                    <InfoRow icon={<CreditCard size={14} />} label="PAN Number" value={detail.pan} mono />
                    <InfoRow icon={<Hash size={14} />} label="GST Number" value={detail.gst} mono />
                  </div>
                </section>

                {/* Bank */}
                <section>
                  <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-stone-400">
                    Bank Details
                  </h3>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <InfoRow icon={<Landmark size={14} />} label="Bank Name" value={detail.bankname} />
                    <InfoRow icon={<Hash size={14} />} label="IFSC Code" value={detail.ifsc} mono />
                    <InfoRow icon={<CreditCard size={14} />} label="Account Number" value={detail.bankaccount} mono />
                  </div>
                </section>

                {detail.kycNote && (
                  <section className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                    <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-amber-700">
                      Latest KYC Note
                    </h3>
                    <p className="text-[12.5px] leading-relaxed text-amber-900">{detail.kycNote}</p>
                  </section>
                )}

                {/* Documents */}
                <section>
                  <h3 className="mb-2.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-400">
                    <FileImage size={13} /> Uploaded Documents
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <DocCard label="Aadhar Front" url={detail.aadhar_f_img} />
                    <DocCard label="Aadhar Back" url={detail.aadhar_b_img} />
                    <DocCard label="PAN Card" url={detail.pan_img} />
                    <DocCard label="Passbook / Cheque" url={detail.passbook_cheque_img} />
                  </div>
                </section>
              </div>

              {/* Right column — actions */}
              <div className="flex flex-col gap-5 lg:col-span-2">
                {/* KYC review */}
                <section className="rounded-2xl border border-stone-200 bg-stone-50/80 p-5">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                      <ShieldCheck size={15} />
                    </div>
                    <h3 className="text-[13px] font-bold text-stone-800">Review KYC</h3>
                  </div>

                  <div className="flex flex-col gap-3.5">
                    <div>
                      <label className="mb-1.5 block text-[11.5px] font-semibold text-stone-500">Decision</label>
                      <select
                        value={kycStatusInput}
                        onChange={e => {
                          setKycStatusInput(e.target.value as KycStatus);
                          setKycSuccess(false);
                        }}
                        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] font-medium text-stone-700 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                      >
                        {KYC_STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[11.5px] font-semibold text-stone-500">
                        Note {kycStatusInput !== 'approved' && <span className="font-normal text-rose-500">· recommended</span>}
                      </label>
                      <textarea
                        value={kycNoteInput}
                        onChange={e => {
                          setKycNoteInput(e.target.value);
                          setKycSuccess(false);
                        }}
                        rows={3}
                        placeholder="e.g. Please upload a clearer image of PAN"
                        className="w-full resize-none rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] text-stone-700 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                      />
                    </div>

                    {kycError && <div className="text-[12px] font-medium text-rose-600">{kycError}</div>}
                    {kycSuccess && !kycError && (
                      <div className="text-[12px] font-medium text-emerald-600">KYC status updated successfully.</div>
                    )}

                    <button
                      onClick={handleKycSubmit}
                      disabled={kycSaving}
                      className="flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {kycSaving && <Loader2 size={14} className="animate-spin" />}
                      Submit Decision
                    </button>
                  </div>
                </section>

                {/* Active toggle */}
                <section className="rounded-2xl border border-stone-200 bg-stone-50/80 p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-200 text-stone-600">
                      <Power size={15} />
                    </div>
                    <h3 className="text-[13px] font-bold text-stone-800">Listing Visibility</h3>
                  </div>

                  <p className="mb-4 text-[12.5px] leading-relaxed text-stone-500">
                    {detail.isActive
                      ? 'This provider is currently active and visible to brand companies.'
                      : 'This provider is currently inactive and hidden from listings.'}
                  </p>

                  {statusError && (
                    <div className="mb-3 text-[12px] font-medium text-rose-600">{statusError}</div>
                  )}

                  <button
                    onClick={handleToggleActive}
                    disabled={statusSaving}
                    className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      detail.isActive ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                  >
                    {statusSaving && <Loader2 size={14} className="animate-spin" />}
                    {detail.isActive ? 'Deactivate Provider' : 'Activate Provider'}
                  </button>
                </section>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderDetailModal;